import { useState, useEffect, useMemo, useRef } from 'react';
import { FiChevronDown, FiSearch } from 'react-icons/fi';
import styles from './LedgerCategorySelect.module.css';

const NATURE_ORDER = { asset: 0, liability: 1, equity: 2, income: 3, expenditure: 4 };

/**
 * Searchable category (ledger sub-group) dropdown.
 * Each option shows the category name with its parent group as muted context,
 * so the group is implied by the chosen category — no separate group picker.
 *
 * Props:
 *   value       — selected sub-group id (string | number)
 *   onChange    — (id: string) => void
 *   options     — array of { value, label, groupLabel, groupNature }
 *   placeholder — trigger label when nothing selected
 *   disabled    — disables the trigger
 */
export default function LedgerCategorySelect({
  value,
  onChange,
  options = [],
  placeholder = '— Select category —',
  disabled = false,
}) {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState('');
  const ref       = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey  = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, []);

  useEffect(() => {
    if (open) { setTimeout(() => searchRef.current?.focus(), 0); }
    else       { setSearch(''); }
  }, [open]);

  const hits = useMemo(() => {
    const q = search.trim().toLowerCase();
    const pool = q
      ? options.filter(o =>
          o.label.toLowerCase().includes(q) ||
          (o.groupLabel || '').toLowerCase().includes(q)
        )
      : options;
    return [...pool].sort((a, b) => {
      const na = NATURE_ORDER[a.groupNature] ?? 99;
      const nb = NATURE_ORDER[b.groupNature] ?? 99;
      return na - nb;
    });
  }, [options, search]);

  const selected = useMemo(
    () => (value != null && value !== '' ? options.find(o => String(o.value) === String(value)) || null : null),
    [value, options],
  );

  return (
    <div ref={ref} className={styles.select}>
      <button
        type="button"
        className={styles.trigger}
        disabled={disabled}
        onClick={() => !disabled && setOpen(o => !o)}
      >
        <span className={`${styles.triggerText} ${!selected ? styles.triggerPlaceholder : ''}`}>
          {selected ? selected.label : placeholder}
        </span>
        {selected?.groupLabel && <span className={styles.triggerBadge}>{selected.groupLabel}</span>}
        <FiChevronDown className={styles.triggerIcon} size={15} />
      </button>

      {open && (
        <div className={styles.panel}>
          <div className={styles.searchRow}>
            <FiSearch size={13} className={styles.searchIcon} />
            <input
              ref={searchRef}
              className={styles.searchInput}
              placeholder="Search category…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onClick={e => e.stopPropagation()}
            />
          </div>

          {hits.length === 0 && <div className={styles.empty}>No categories found</div>}

          {hits.map(o => {
            const isSel = String(o.value) === String(value);
            return (
              <div
                key={o.value}
                className={`${styles.option} ${isSel ? styles.optionSelected : ''}`}
                onClick={() => { onChange(String(o.value)); setOpen(false); }}
              >
                <span className={styles.optionName}>{o.label}</span>
                {o.groupLabel && <span className={styles.optionGroup}>{o.groupLabel}</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
